// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { ActionIcon, Text } from '@mantine/core';

import type { NavbarFooterItemProps } from 'app/Layout/NavBar/NavBarContent/NavBarFooter';
import { NavPopover } from 'app/Layout/NavBar/NavBarContent/NavPopover';
import { StyledNavLink } from 'app/Layout/NavBar/NavBarContent/StyledNavLink';
import { navbarTransitionStyle } from 'app/Layout/NavBar/useAppShell';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { useBlockerContext } from 'hooks/navigation/useBlockerContext';
import { useUser } from 'hooks/user/useUser';
import { useSignOut } from 'modules/Authentication/auth/useSignOut';

import { EditProfileModal } from './EditProfileModal/EditProfileModal';
import { NotificationSettingsModal } from './NotificationSettingsModal/NotificationSettingsModal';
import { ResetPasswordModal } from './ResetPasswordModal/ResetPasswordModal';

type ModalType = 'Edit Profile' | 'Notification Settings' | 'Reset Password';

export const ProfileDropdown = ({ navbarExpanded, popoverOpened, onPopoverChanged }: NavbarFooterItemProps) => {
  const [signOutBlocked, setSignOutBlocked] = useState(false);
  const { isBlocked, unblockAll } = useBlockerContext();
  const { user } = useUser();
  const signOut = useSignOut();

  const [activeModalType, setActiveModalType] = useState<ModalType>();

  const openModal = (modalType: ModalType) => () => {
    setActiveModalType(modalType);
    onPopoverChanged(false);
  };

  return (
    <>
      <NavPopover
        opened={popoverOpened}
        onChange={onPopoverChanged}
        position={navbarExpanded ? 'top' : 'right-end'}
        label={validateUserNameOrEmail(user)}
        target={
          <ActionIcon
            className="nav-link-icon-collapsed"
            color="gray"
            size="input-sm"
            style={{
              'transition': navbarTransitionStyle,
              '--ai-hover': 'var(--mantine-color-app-shell-gray-7)',
            }}
          >
            <EvolveIcon icon="Profile" />
          </ActionIcon>
        }
      >
        <StyledNavLink label="Edit Profile" icon="Edit" onClick={openModal('Edit Profile')} />
        <StyledNavLink label="Notification Settings" icon="Notification" onClick={openModal('Notification Settings')} />
        {!user.isSsoUser && (
          <StyledNavLink label="Reset Password" icon="ResetPassword" onClick={openModal('Reset Password')} />
        )}
        <StyledNavLink label="Sign Out" icon="Exit" onClick={() => (isBlocked ? setSignOutBlocked(true) : signOut())} />
      </NavPopover>
      <ResetPasswordModal opened={activeModalType === 'Reset Password'} onClose={() => setActiveModalType(undefined)} />
      <EditProfileModal
        user={user}
        opened={activeModalType === 'Edit Profile'}
        onClose={() => setActiveModalType(undefined)}
      />
      <NotificationSettingsModal
        opened={activeModalType === 'Notification Settings'}
        onClose={() => setActiveModalType(undefined)}
      />
      <WrappedModal
        title="Sign out"
        opened={signOutBlocked}
        onClose={() => setSignOutBlocked(false)}
        buttonColor="red"
        onConfirm={async () => {
          unblockAll();
          await signOut();
        }}
        closeOnConfirm={false}
        confirmationText="Sign out"
      >
        <Text>You have unsaved changes. Are you sure you want to sign out?</Text>
      </WrappedModal>
    </>
  );
};
